// The request and option rules of AsyncCombobox (async-combobox.tsx), lifted unchanged from
// `src/components/async-select/AsyncSelect.tsx` (which stays as it is for its other importers). The two share React
// Query keys on purpose: an AsyncCombobox and an AsyncSelect on the same path read and fill the same cache.
// async-combobox-logic.test.ts checks each rule against verbatim copies of AsyncSelect's code.
import get from 'lodash/get';

/** One row of the list. `origin` is the response record (null for a placeholder row). */
export interface AsyncComboboxOption {
  label: string;
  value: string;
  origin: unknown;
  key: string;
}

/** The input's id when none is given: AsyncSelect's `stableId` (`/api/v1/ports` → `async-select--api-v1-ports`). */
export const asyncComboboxId = (path: string) => `async-select-${path.replace(/\W+/g, '-')}`;

/** `noCache`, or a path whose lists change often (uoms, packages): no React Query cache. */
export const bypassesCache = (path: string, noCache = false) => noCache || /\/(uoms|packages)/.test(path);

/** The options list's cache times: 0 when bypassed, 24 h for the HS codes, else 12 h; gc 0 or 12 h. */
export function optionsCacheTimes(path: string, noCache = false): { staleTime: number; gcTime: number } {
  const bypass = bypassesCache(path, noCache);
  return {
    staleTime: bypass ? 0 : path.includes('get-all-hscode') ? 24 * 60 * 60 * 1000 : 12 * 60 * 60 * 1000,
    gcTime: bypass ? 0 : 12 * 60 * 60 * 1000,
  };
}
/** The default-label query's cache times (always cached). */
export const DEFAULT_LABEL_CACHE = { staleTime: 12 * 60 * 60 * 1000, gcTime: 12 * 60 * 60 * 1000 } as const;

/** `['async-select-options', path, query, additionalParam]`, AsyncSelect's key. */
export const optionsQueryKey = (path: string, query: string, additionalParam: string) =>
  ['async-select-options', path, query, additionalParam] as const;
/** `['async-select-default', defaultValuePath, value]`, AsyncSelect's key. */
export const defaultLabelQueryKey = (defaultValuePath: string | undefined, value: string) =>
  ['async-select-default', defaultValuePath, value] as const;

/**
 * The options GET's URL: `{path}?offset=0&limit=10&{key}={query}` then `&{additionalParam}` when given. The query is
 * not encoded. Without `useDefault` only `additionalParam` is sent (or nothing).
 */
export function optionsUrl({
  path,
  query,
  dynamicKeyParam = 'query',
  additionalParam = '',
  useDefault = true,
}: {
  path: string;
  query: string;
  dynamicKeyParam?: string;
  additionalParam?: string;
  useDefault?: boolean;
}): string {
  const defaultParams = useDefault ? `offset=0&limit=10&${dynamicKeyParam}=${query}&` : '';
  const queryString = additionalParam ? `${defaultParams}${additionalParam}` : defaultParams.slice(0, -1);
  return `${path}${queryString ? `?${queryString}` : ''}`;
}

/** The default-label GET's URL: `{defaultValuePath}{value}`, not encoded. */
export const defaultLabelUrl = (defaultValuePath: string, value: string) => `${defaultValuePath}${value}`;

/** The text the list fetches with: '' for a full list (`showFullList`, `useLocalQuery`), else the debounced search. */
export const queryForFetch = ({
  showFullList,
  useLocalQuery,
  debouncedSearch,
}: {
  showFullList: boolean;
  useLocalQuery: boolean;
  debouncedSearch: string;
}) => (showFullList || useLocalQuery ? '' : debouncedSearch);

/**
 * Whether the options list may fetch: after the first paint, not disabled, and either the whole list is needed
 * (`useLocalQuery`), the field was focused once (`hasOpened`, or `loadOnMount`), or a value has no other way to get
 * its label (no `defaultValuePath` and no `initialSearchValue`).
 */
export function listEnabled({
  rendered,
  disabled,
  fetchDisabled = false,
  useLocalQuery,
  hasOpened,
  value,
  defaultValuePath,
  initialSearchValue,
}: {
  rendered: boolean;
  disabled: boolean;
  /** AsyncSelect's `data` seed: the list is `extraOptions` only, no list GET. */
  fetchDisabled?: boolean;
  useLocalQuery: boolean;
  hasOpened: boolean;
  value: string;
  defaultValuePath?: string;
  initialSearchValue: string;
}): boolean {
  return (
    rendered &&
    !disabled &&
    !fetchDisabled &&
    (useLocalQuery || hasOpened || (!!value && !defaultValuePath && !initialSearchValue))
  );
}

/** A static option offered beside the fetched ones (AsyncSelect's `extraOptions`): the upload kit's "OF - Ocean Freight". */
export type ExtraOption = { label: string; value: string };
export const NO_EXTRA_OPTIONS: ReadonlyArray<ExtraOption> = [];
/** Filters and orders options by a search text (AsyncSelect's `filterOptions`). */
export type OptionsFilter = <T extends { label: unknown }>(options: T[], search: string) => T[];
/** The custom option `value` names, if any. */
export const findExtraOption = (extraOptions: ReadonlyArray<ExtraOption>, value: string): ExtraOption | null =>
  extraOptions.find((opt) => String(opt.value) === String(value)) ?? null;

/** Whether the default-label GET may run: a value, a `defaultValuePath`, no `initialSearchValue`, not already a label. */
export function defaultLabelEnabled({
  rendered,
  disabled,
  value,
  defaultValuePath,
  initialSearchValue,
  extraOptions = NO_EXTRA_OPTIONS,
}: {
  rendered: boolean;
  disabled: boolean;
  value: string;
  defaultValuePath?: string;
  initialSearchValue: string;
  extraOptions?: ReadonlyArray<ExtraOption>;
}): boolean {
  return (
    rendered &&
    !disabled &&
    !!value &&
    !!defaultValuePath &&
    !initialSearchValue &&
    // the endpoint holds no row for a custom option: its label comes from the option itself
    !findExtraOption(extraOptions, value) &&
    !(typeof value === 'string' && value.includes(' - '))
  );
}

/** A record's label: the `optionsLabelKeys` values that exist, joined with ` - `. */
export const recordLabel = (record: unknown, optionsLabelKeys: readonly string[]) =>
  optionsLabelKeys
    .map((k) => get(record, k))
    .filter((v) => v !== undefined)
    .join(' - ');

/** A response's `data` (one record or a list) as options, de-duped by value + label, filtered by `localFilter`. */
export function mapToOptions(
  data: unknown,
  {
    optionsLabelKeys,
    valueOnResponseKey,
    stableId,
    localFilter,
  }: { optionsLabelKeys: readonly string[]; valueOnResponseKey: string; stableId: string; localFilter?: string }
): AsyncComboboxOption[] {
  const payload = (data as { data?: unknown } | null | undefined)?.data;
  if (!payload) return [];
  const items: unknown[] = Array.isArray(payload) ? payload : [payload];
  if (!items.length) return [];
  const mapped = items.map((r, index) => ({
    label: recordLabel(r, optionsLabelKeys),
    value: get(r, valueOnResponseKey) as string,
    origin: r,
    key: `${stableId}-${get(r, valueOnResponseKey)}-${index}`,
  }));
  const deduped = mapped.filter(
    (opt, idx, arr) => arr.findIndex((o) => o.value === opt.value && o.label === opt.label) === idx
  );
  return localFilter !== undefined
    ? deduped.filter((opt) => opt.label.toLowerCase().includes(localFilter.toLowerCase()))
    : deduped;
}

/** The label the default-label response gives the value ('' when it has none). */
export function defaultLabelText(defaultData: unknown, optionsLabelKeys: readonly string[]): string {
  const payload = (defaultData as { data?: unknown } | null | undefined)?.data;
  const item = Array.isArray(payload) ? payload[0] : payload;
  return recordLabel(item, optionsLabelKeys);
}

/** What the list shows, AsyncSelect's `options` memo (no `data`, `extraOptions` or `filterOptions`). */
export function deriveOptions({
  disabled,
  value,
  initialSearchValue,
  useLocalQuery,
  showFullList,
  searchQuery,
  debouncedSearch,
  optionsData,
  defaultData,
  optionsLabelKeys,
  valueOnResponseKey,
  stableId,
  extraOptions = NO_EXTRA_OPTIONS,
  filterOptions,
}: {
  disabled: boolean;
  value: string;
  initialSearchValue: string;
  useLocalQuery: boolean;
  showFullList: boolean;
  searchQuery: string;
  debouncedSearch: string;
  optionsData: unknown;
  defaultData: unknown;
  optionsLabelKeys: readonly string[];
  valueOnResponseKey: string;
  stableId: string;
  /** Static options listed before the fetched ones (AsyncSelect's `extraOptions`), filtered by the search text. */
  extraOptions?: ReadonlyArray<ExtraOption>;
  /** With `useLocalQuery`: filters and ranks the whole list (custom + fetched) by the search text, in place of the
   *  plain substring filter (AsyncSelect's `filterOptions`, e.g. the upload kit's `rankDocTypeOptions`). */
  filterOptions?: OptionsFilter;
}): AsyncComboboxOption[] {
  const map = (data: unknown, localFilter?: string) =>
    mapToOptions(data, { optionsLabelKeys, valueOnResponseKey, stableId, localFilter });
  const extras = (filter?: string): AsyncComboboxOption[] =>
    extraOptions
      .filter((opt) => !filter || opt.label.toLowerCase().includes(filter.toLowerCase()))
      .map((opt) => ({ ...opt, origin: null, key: `${stableId}-${opt.value}-extra` }));
  if (disabled) {
    const disabledLabel = initialSearchValue || value;
    return value || initialSearchValue
      ? [{ label: disabledLabel, value, origin: null, key: `${stableId}-${value || 'empty'}-disabled` }]
      : [];
  }
  if (useLocalQuery) {
    if (filterOptions) {
      const all = [...extras(), ...map(optionsData)];
      // The input shows the selected label until the person types; that text isn't a search, so open on the
      // whole list, as the fetched mode does.
      const showsSelection = all.some((opt) => String(opt.value) === String(value) && opt.label === searchQuery);
      return filterOptions(all, showsSelection ? '' : searchQuery);
    }
    return [...extras(searchQuery), ...map(optionsData, searchQuery)];
  }
  if (showFullList || debouncedSearch) return [...extras(debouncedSearch), ...map(optionsData)];
  // no active search and the value is a custom option: it carries its own label
  const selectedExtra = findExtraOption(extraOptions, value);
  if (selectedExtra) return [{ ...selectedExtra, origin: null, key: `${stableId}-${selectedExtra.value}-extra` }];
  const defaultPayload = (defaultData as { data?: unknown } | null | undefined)?.data;
  if (defaultPayload) {
    const item = Array.isArray(defaultPayload) ? defaultPayload[0] : defaultPayload;
    const labelOpt = recordLabel(item, optionsLabelKeys);
    return [
      {
        label: labelOpt || value,
        value: (get(item, valueOnResponseKey) as string) || value,
        origin: item,
        key: `${stableId}-${get(item, valueOnResponseKey)}-default`,
      },
    ];
  }
  if (value) {
    const fallbackLabel = initialSearchValue || value;
    return [{ label: fallbackLabel, value, origin: null, key: `${stableId}-${value}-fallback` }];
  }
  return [...extras(), ...map(optionsData)];
}
